import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Search,
  Filter,
  Calendar,
  User,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { debugLog } from '../services/debugConfig';
import { Advertisement, MarketingCampaign, Partner, PartnerCampaign, PromoCodeRequest } from '../types';

type DateFilterOption = '7days' | '14days' | '30days' | 'current_month' | 'previous_month';
type StatusFilterOption = 'all' | 'sent' | 'pending' | 'declined' | 'redirected';

export const PromoRequestsManager: React.FC = () => {
  const { 
    user,
    promoCodeRequests,
    partnerCampaigns,
    marketingCampaigns,
    partners,
    advertisements,
    updatePromoCodeRequest
  } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>('all');
  const [dateFilter, setDateFilter] = useState<DateFilterOption | null>('current_month');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  // Date filter helpers
  const getDateFilterRange = (filter: DateFilterOption): { start: Date; end: Date } => {
    const now = new Date();
    
    if (filter === '7days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }

    if (filter === '14days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 14);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }

    if (filter === '30days') {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      return { start, end: now };
    }
    
    if (filter === 'current_month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start, end };
    }
    
    // previous_month
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return { start, end };
  };

  const toggleDateFilter = (filter: DateFilterOption) => {
    setDateFilter((prev: DateFilterOption | null) => (prev === filter ? null : filter));
  };

  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
  sixtyDaysAgo.setHours(0, 0, 0, 0);

  useEffect(() => {
    (async () => {
      await debugLog('PromoRequestsManager - All promo requests:', promoCodeRequests);
      await debugLog('PromoRequestsManager - User:', user);
    })();
  }, [promoCodeRequests, user]);

  const typedRequests = promoCodeRequests as PromoCodeRequest[];
  const typedPartnerCampaigns = partnerCampaigns as PartnerCampaign[];
  const typedMarketingCampaigns = marketingCampaigns as MarketingCampaign[];
  const typedPartners = partners as Partner[];
  const typedAdvertisements = advertisements as Advertisement[];

  const filteredRequests = typedRequests.filter((request: PromoCodeRequest) => {
    // Filter by partner for partner admins - use partner_id directly from request
    if (user?.role === 'partner_admin') {
      // Primary check: use partner_id stored directly on the request
      const partnerMatch = request.partner_id === user.partner_id;
      
      // Fallback: check via advertisement if partner_id is missing
      let fallbackMatch = false;
      if (!request.partner_id && request.advertisement_id) {
        const advertisement = typedAdvertisements.find((ad: Advertisement) => ad.id === request.advertisement_id);
        fallbackMatch = advertisement?.partner_id === user.partner_id;
      }
      
      void debugLog('PromoRequestsManager - Checking request', {
        requestEmail: request.email,
        requestPartnerId: request.partner_id,
        userPartnerId: user.partner_id,
        partnerMatch: partnerMatch || fallbackMatch
      });
      
      if (!partnerMatch && !fallbackMatch) return false;
    }
    
    // Filter out requests older than 60 days
    const requestDate = new Date(request.requested_at);
    if (requestDate < sixtyDaysAgo) return false;
    
    // Apply date filter when selected
    if (dateFilter) {
      const { start, end } = getDateFilterRange(dateFilter);
      if (requestDate < start || requestDate > end) return false;
    }
    
    const matchesSearch = request.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    (async () => {
      await debugLog('PromoRequestsManager - Filtered requests:', filteredRequests);
    })();
  }, [filteredRequests]);

  const getStatusLabel = (status: string) => {
    if (status === 'promo_pending') return 'Pending';
    if (status === 'promo_provided') return 'Sent';
    if (status === 'declined') return 'Declined';
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
      case 'promo_pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-green-100 text-green-800';
      case 'redirected': return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'declined': return 'bg-red-100 text-red-800';
      case 'promo_provided': return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'promo_pending':
        return <Clock size={16} />;
      case 'sent': return <CheckCircle size={16} />;
      case 'redirected': return <CheckCircle size={16} />;
      case 'expired': return <XCircle size={16} />;
      case 'declined': return <XCircle size={16} />;
      case 'promo_provided': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const handleStatusChange = async (requestId: string, newStatus: 'promo_provided' | 'declined') => {
    setUpdatingStatus(requestId);
    try {
      await updatePromoCodeRequest(requestId, { status: newStatus });
      // State is updated automatically through context
      setOpenDropdown(null);
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const canChangeStatus = (request: PromoCodeRequest) => {
    // Only allow status changes for pending/promo_pending requests (no promocode ads)
    // And only for partner/global admins
    return (
      (user?.role === 'partner_admin' || user?.role === 'global_admin') &&
      (request.status === 'promo_pending' || request.status === 'pending')
    );
  };

  const exportToCSV = () => {
    const headers = [
      'Email',
      'Campaign',
      ...(user?.role === 'global_admin' ? ['Partner'] : []),
      'Marketing Campaign',
      'Selected Advertisement',
      'Promo Code',
      'Privacy Agreement',
      'Status',
      'Requested Date'
    ];

    const csvData: string[][] = filteredRequests.map((request: PromoCodeRequest) => {
      const hasActualCode =
        request.promo_code &&
        request.promo_code !== 'No promocode' &&
        request.promo_code !== 'Redirect';
      return [
        String(request.email),
        String(getCampaignName(request)),
        ...(user?.role === 'global_admin' ? [String(getPartnerName(request))] : []),
        String(getMarketingCampaignName(request)),
        String(getAdvertisementName(request)),
        hasActualCode ? String(request.promo_code ?? 'No code') : 'No code',
        request.agreed_to_privacy ? 'Agreed' : 'Not Agreed',
        String(getStatusLabel(request.status)),
        new Date(request.requested_at).toLocaleDateString()
      ];
    });

    const csvContent = [headers, ...csvData]
      .map((row: string[]) => row.map((field: string) => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `promo-requests-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCampaignName = (request: PromoCodeRequest) => {
    // First try stored campaign_name (denormalized field)
    if (request.campaign_name) {
      return request.campaign_name;
    }
    
    // Fallback: Method 1: Direct lookup by partner_campaign_id
    let campaign = typedPartnerCampaigns.find((pc: PartnerCampaign) => pc.id === request.partner_campaign_id);
    
    // Fallback: Method 2: If not found, find campaign that contains this advertisement_id
    const advertisementId = request.advertisement_id;
    if (!campaign && advertisementId) {
      campaign = typedPartnerCampaigns.find((pc: PartnerCampaign) => 
        pc.advertisement_ids && pc.advertisement_ids.includes(advertisementId)
      );
    }
    
    // If no campaign found, check if this is a direct view request (no partner_campaign_id)
    if (!campaign && !request.partner_campaign_id) {
      return 'Direct view';
    }
    
    return campaign?.name || 'Unknown Campaign';
  };

  const getPartnerName = (request: PromoCodeRequest) => {
    // Method 1: Direct lookup by partner_id
    let partner = typedPartners.find((p: Partner) => p.id === request.partner_id);
    
    // Method 2: If not found, try to get partner from advertisement
    if (!partner && request.advertisement_id) {
      const advertisement = typedAdvertisements.find((ad: Advertisement) => ad.id === request.advertisement_id);
      if (advertisement) {
        partner = typedPartners.find((p: Partner) => p.id === advertisement.partner_id);
      }
    }
    
    return partner?.name || 'Unknown Partner';
  };

  const getMarketingCampaignName = (request: PromoCodeRequest) => {
    // Lookup marketing campaign name from marketing_campaign_id
    if (!request.marketing_campaign_id) return '';
    const campaign = typedMarketingCampaigns.find((mc: MarketingCampaign) => mc.id === request.marketing_campaign_id);
    return campaign?.name || 'Unknown Marketing Campaign';
  };
  
  const getAdvertisementName = (request: PromoCodeRequest) => {
    // Try stored name first, then lookup from advertisement
    if (request.advertisement_name) {
      return request.advertisement_name;
    }
    if (request.advertisement_id) {
      const advertisement = typedAdvertisements.find((ad: Advertisement) => ad.id === request.advertisement_id);
      return advertisement?.name || 'No advertisement selected';
    }
    return 'No advertisement selected';
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Promo Code Requests</h2>
        <p className="text-gray-600 mt-2">
          {user?.role === 'partner_admin' 
            ? 'Manage promo code requests for your campaigns'
            : 'View all promo code requests across all partners'
          }
        </p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Requests"
          value={filteredRequests.length}
          icon={<Mail className="text-white" size={24} />}
          color="bg-blue-500"
        />
        <MetricCard
          title="Pending"
          value={filteredRequests.filter(r => r.status === 'pending').length}
          icon={<Clock className="text-white" size={24} />}
          color="bg-yellow-500"
        />
        <MetricCard
          title="Sent"
          value={filteredRequests.filter(r => r.status === 'sent').length}
          icon={<CheckCircle className="text-white" size={24} />}
          color="bg-green-500"
        />
        <MetricCard
          title="Expired"
          value={filteredRequests.filter(r => r.status === 'expired').length}
          icon={<XCircle className="text-white" size={24} />}
          color="bg-red-500"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-wrap gap-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilterOption)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-transparent appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="declined">Declined</option>
              <option value="redirected">Redirected</option>
            </select>
          </div>
          {/* Date Quick Filters */}
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <button
              onClick={() => toggleDateFilter('7days')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition duration-200 ${
                dateFilter === '7days'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 7 days
            </button>
            <button
              onClick={() => toggleDateFilter('14days')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition duration-200 ${
                dateFilter === '14days'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 14 days
            </button>
            <button
              onClick={() => toggleDateFilter('30days')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition duration-200 ${
                dateFilter === '30days'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Last 30 days
            </button>
            <button
              onClick={() => toggleDateFilter('current_month')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition duration-200 ${
                dateFilter === 'current_month'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Current month
            </button>
            <button
              onClick={() => toggleDateFilter('previous_month')}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition duration-200 ${
                dateFilter === 'previous_month'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Previous month
            </button>
          </div>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition duration-200"
        >
          <Download size={20} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                {user?.role === 'global_admin' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Advertisement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promo Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Privacy Agreement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requested
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <User className="text-gray-400 mr-2" size={16} />
                      <span className="text-sm font-medium text-gray-900">{request.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {getCampaignName(request)}
                  </td>
                  {user?.role === 'global_admin' && (
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {getPartnerName(request)}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>
                      <div className="font-medium text-gray-900">
                        {getAdvertisementName(request)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {getMarketingCampaignName(request)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {request.promo_code && request.promo_code !== 'No promocode' && request.promo_code !== 'Redirect' ? (
                      <span className="font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        {request.promo_code}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">No code</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      request.agreed_to_privacy 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {request.agreed_to_privacy ? 'Agreed' : 'Not Agreed'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1">{getStatusLabel(request.status)}</span>
                      </span>
                      {canChangeStatus(request) && (
                        <div className="relative">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === request.id ? null : request.id)}
                            disabled={updatingStatus === request.id}
                            className="p-1 hover:bg-gray-100 rounded transition disabled:opacity-50"
                            title="Change status"
                          >
                            <ChevronDown size={16} className="text-gray-600" />
                          </button>
                          {openDropdown === request.id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                              <button
                                onClick={() => handleStatusChange(request.id, 'promo_provided')}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-emerald-50 flex items-center gap-2 rounded-t-lg"
                              >
                                <CheckCircle size={14} className="text-emerald-600" />
                                <span>Code Provided</span>
                              </button>
                              <button
                                onClick={() => handleStatusChange(request.id, 'declined')}
                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 rounded-b-lg"
                              >
                                <XCircle size={14} className="text-red-600" />
                                <span>Declined</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="text-gray-400 mr-2" size={16} />
                      {new Date(request.requested_at).toLocaleDateString()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <Mail size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No promo requests found</h3>
          <p className="text-gray-500">
            {searchTerm || dateFilter || statusFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'No users have requested promo codes yet'
            }
          </p>
        </div>
      )}
    </div>
  );
};